import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { watchAuthState } from '../lib/firebase'
import { kycApi, type KycStatusResponse } from '../api/endpoints'
import { useIdentity } from '../lib/identity'

/**
 * Slim under-header status strip for signed-in-but-unverified users —
 * the desktop answer to "why are prices locked?". Three states:
 *   submitted     → "being verified, 1–2 business days"
 *   rejected      → reason + resubmit link
 *   not_submitted → finish-verification prompt (legacy pre-wizard accounts)
 * Approved users and anonymous visitors render nothing.
 *
 * Status is cached per session (module-level) so route changes don't
 * refetch; sign-out clears it via the auth watcher.
 */
let statusCache: Promise<KycStatusResponse | null> | null = null

function fetchStatus(): Promise<KycStatusResponse | null> {
  if (!statusCache) {
    statusCache = kycApi.status().catch(() => null)
  }
  return statusCache
}

export function VerifyingBanner() {
  const [status, setStatus] = useState<KycStatusResponse | null>(null)
  const identity = useIdentity()

  useEffect(() => {
    let cancelled = false
    const unsub = watchAuthState((user) => {
      if (!user) {
        statusCache = null
        if (!cancelled) setStatus(null)
        return
      }
      void fetchStatus().then((s) => { if (!cancelled) setStatus(s) })
    })
    return () => { cancelled = true; unsub() }
  }, [])

  // Admins are verified by definition (server exempts them for data too) — they
  // must never see a "get verified" prompt regardless of their own KYC flag.
  if (identity.isAdmin) return null
  if (!status || status.status === 'approved') return null

  if (status.status === 'rejected') {
    return (
      <div className="bg-danger/5 border-b border-danger/20" data-testid="verifying-banner-rejected">
        <div className="mx-auto max-w-6xl px-6 py-2.5 flex items-center justify-between gap-4 text-sm">
          <p className="text-danger">
            <span className="font-semibold">Verification was declined.</span>{' '}
            {status.message.replace(/^Verification was declined:?\s*/i, '').replace(/\s*You can resubmit\.$/i, '')}
          </p>
          <Link to="/kyc" className="shrink-0 font-semibold text-danger underline underline-offset-2 hover:no-underline">
            Resubmit
          </Link>
        </div>
      </div>
    )
  }

  if (status.status === 'submitted') {
    return (
      <div className="bg-gold-050 border-b border-gold-500/30" data-testid="verifying-banner">
        <div className="mx-auto max-w-6xl px-6 py-2.5 flex items-center gap-2.5 text-sm text-ink">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-gold-600" aria-hidden>
            <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
          </svg>
          <p>
            <span className="font-semibold">Your account is being verified</span> — please allow 1–2 business
            days. Prices and supplier contact unlock the moment you're approved.
          </p>
        </div>
      </div>
    )
  }

  // not_submitted — an account that never finished verification.
  return (
    <div className="bg-cream border-b border-line" data-testid="verifying-banner-incomplete">
      <div className="mx-auto max-w-6xl px-6 py-2.5 flex items-center justify-between gap-4 text-sm">
        <p className="text-navy-500">Finish verification to unlock prices and supplier contact.</p>
        <Link to="/kyc" className="shrink-0 font-semibold text-gold-600 hover:text-gold-500">
          Get verified →
        </Link>
      </div>
    </div>
  )
}
